import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, Calendar, ChevronRight, Tag, User } from "lucide-react";
import { BlogArticleBody } from "@/components/blog/BlogArticleBody";
import { resolveBlogInlineCta } from "@/content/blogInlineCta";
import { BlogArticleHero } from "@/components/blog/BlogArticleHero";
import { BlogComments } from "@/components/blog/BlogComments";
import { BlogRelatedPosts } from "@/components/blog/BlogRelatedPosts";
import { Container } from "@/components/ui/primitives";
import { useApp } from "@/context/AppProviders";
import { useReveal } from "@/hooks/useReveal";
import { usePageSeo } from "@/hooks/usePageSeo";
import { fetchBlogPost } from "@/lib/blogApi";
import { consumeBlogBootstrap, invalidateBlogBootstrap, peekBlogBootstrap } from "@/lib/blogBootstrap";
import { clearPaintShell } from "@/lib/clearPaintShell";
import { blogCategoryPath, blogListPath, blogPostPath, homePath, tagPath } from "@/lib/routes";
import {
  blogPostingJsonLd,
  breadcrumbJsonLd,
  formatBlogPostTitle,
  truncateDescription,
} from "@/lib/seo";
import { cn } from "@/lib/utils";
import type { BlogComment, BlogPost } from "@/types/blog";

function BlogPostBreadcrumb({
  locale,
  title,
  isTr,
}: {
  locale: "tr" | "en";
  title?: string;
  isTr: boolean;
}) {
  return (
    <nav className="mb-5 text-sm text-[var(--text-sm)] sm:mb-6" aria-label="Breadcrumb">
      <ol className="flex min-w-0 flex-wrap items-center gap-1.5">
        <li className="shrink-0">
          <Link to={homePath(locale)} className="no-underline transition hover:text-[var(--text)]">
            {isTr ? "Ana Sayfa" : "Home"}
          </Link>
        </li>
        <ChevronRight className="size-3.5 shrink-0 opacity-50" aria-hidden />
        <li className="shrink-0">
          <Link to={blogListPath(locale)} className="no-underline transition hover:text-[var(--text)]">
            Blog
          </Link>
        </li>
        {title ? (
          <>
            <ChevronRight className="hidden size-3.5 shrink-0 opacity-50 md:block" aria-hidden />
            <li
              className="hidden min-w-0 max-w-full font-medium text-[var(--text)] md:block"
              aria-current="page"
              title={title}
            >
              <span className="block truncate">{title}</span>
            </li>
          </>
        ) : null}
      </ol>
    </nav>
  );
}

function initialPostBootstrap(slug: string | undefined) {
  const boot = peekBlogBootstrap();
  if (boot?.type === "post" && slug && boot.slug === slug) {
    return boot;
  }
  return null;
}

export function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const { locale } = useApp();
  const isTr = locale === "tr";
  const seeded = initialPostBootstrap(slug);
  const [post, setPost] = useState<BlogPost | null>(seeded?.post ?? null);
  const [comments, setComments] = useState<BlogComment[]>(seeded?.comments ?? []);
  const [loading, setLoading] = useState(!seeded);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState(false);

  useReveal([locale, slug, post?.id]);

  useEffect(() => {
    if (post) clearPaintShell();
  }, [post]);

  useEffect(() => {
    if (!slug) return;

    const postSlug = slug;
    let cancelled = false;

    const boot = consumeBlogBootstrap();
    if (boot?.type === "post" && boot.slug === postSlug) {
      setPost(boot.post);
      setComments(boot.comments);
      setNotFound(false);
      setError(false);

      // Shell bootstrap: içerik HTML boş olabilir — arka planda tamamla
      if (!boot.post.contentHtml) {
        setLoading(false);
        void fetchBlogPost(postSlug).then((result) => {
          if (cancelled) return;
          if (result.ok && result.data) {
            setPost(result.data.post);
            setComments(result.data.comments);
          }
        });
        return () => {
          cancelled = true;
        };
      }

      setLoading(false);
      return () => {
        cancelled = true;
      };
    }

    invalidateBlogBootstrap();

    async function load() {
      setLoading(true);
      setNotFound(false);
      setError(false);

      const result = await fetchBlogPost(postSlug);
      if (cancelled) return;

      if (result.ok && result.data) {
        setPost(result.data.post);
        setComments(result.data.comments);
      } else if (result.error === "not_found") {
        setPost(null);
        setComments([]);
        setNotFound(true);
      } else {
        setPost(null);
        setComments([]);
        setError(true);
      }

      setLoading(false);
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const inlineCta = useMemo(() => {
    if (!post) return null;
    return resolveBlogInlineCta(locale, {
      tagSlugs: post.tags.map((tag) => tag.slug),
      categorySlug: post.categorySlug,
      categoryLabel: post.category,
    });
  }, [post, locale]);

  const postPath = slug ? blogPostPath(locale, slug) : undefined;

  usePageSeo(
    useMemo(() => {
      if (!post || !postPath) return null;
      return {
        title: formatBlogPostTitle(post.title),
        description: truncateDescription(post.excerpt || post.title),
        canonicalPath: postPath,
        ogImage: post.imageUrl || undefined,
        ogType: "article" as const,
        locale,
        robots: "index, follow",
        publisher: "Enabase",
        jsonLd: [
          breadcrumbJsonLd([
            { name: isTr ? "Ana Sayfa" : "Home", path: homePath(locale) },
            { name: "Blog", path: blogListPath(locale) },
            { name: post.title, path: postPath },
          ]),
          blogPostingJsonLd({
            title: post.title,
            description: post.excerpt || post.title,
            path: postPath,
            imageUrl: post.imageUrl,
            datePublished: post.dateTime,
            author: post.author,
          }),
        ],
      };
    }, [isTr, locale, post, postPath])
  );

  if (!slug) {
    return <Navigate to={blogListPath(locale)} replace />;
  }

  if (!loading && notFound) {
    return <Navigate to={blogListPath(locale)} replace />;
  }

  return (
    <div className="bg-[var(--bg)] pt-[72px] pb-16 sm:pb-20 lg:pb-24">
      <Container className="page-hero-inner">
        <BlogPostBreadcrumb locale={locale} title={post?.title} isTr={isTr} />

        {loading ? (
          <p className="text-sm text-[var(--text-md)]">{isTr ? "Yazı yükleniyor…" : "Loading post…"}</p>
        ) : null}

        {!loading && error ? (
          <p className="rounded-xl border border-red-500/25 bg-red-500/10 px-4 py-6 text-sm text-red-700 dark:text-red-300">
            {isTr
              ? "Yazı yüklenemedi. Veritabanı bağlantısını kontrol edin."
              : "Could not load this post. Please check the database connection."}
          </p>
        ) : null}

        {post ? (
          <article className="blog-article-page min-w-0">
            <header className="mx-auto mb-8 max-w-3xl sm:mb-10">
              <h1 className="text-[1.75rem] font-bold leading-[1.2] tracking-tight text-[var(--text)] sm:text-4xl lg:text-[2.625rem]">
                {post.title}
              </h1>

              {(post.date || post.author) && (
                <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 sm:mt-6">
                  {post.date ? (
                    <time
                      dateTime={post.dateTime}
                      className="inline-flex items-center gap-2 text-sm text-[var(--text-md)] sm:text-[0.9375rem]"
                    >
                      <Calendar className="size-4 shrink-0 opacity-70" aria-hidden />
                      <span className="font-medium text-[var(--text)]">{post.date}</span>
                    </time>
                  ) : null}
                  {post.category && post.categorySlug ? (
                    <Link
                      to={blogCategoryPath(locale, post.categorySlug)}
                      className="inline-flex items-center rounded-full border border-[var(--border)] bg-[var(--card)] px-3 py-1 text-xs font-semibold text-[var(--text-md)] no-underline transition hover:border-enabase-blue/30 hover:text-accent-link"
                    >
                      {post.category}
                    </Link>
                  ) : null}
                  {post.author ? (
                    <span className="inline-flex items-center gap-2 text-sm text-[var(--text-md)] sm:text-[0.9375rem]">
                      <User className="size-4 shrink-0 opacity-70" aria-hidden />
                      <span>
                        <span className="sr-only">{isTr ? "Yazar: " : "Author: "}</span>
                        <span className="font-medium text-[var(--text)]">{post.author}</span>
                      </span>
                    </span>
                  ) : null}
                </div>
              )}

              {post.excerpt ? (
                <p className="mt-5 text-lg leading-relaxed text-[var(--text)]/85 sm:text-xl sm:leading-[1.65]">
                  {post.excerpt}
                </p>
              ) : null}
            </header>

            {post.imageUrl ? <BlogArticleHero src={post.imageUrl} alt={post.title} /> : null}

            <BlogArticleBody
              contentHtml={post.contentHtml}
              blocks={post.blocks}
              className="mx-auto max-w-3xl"
              enableInlineCta={Boolean(inlineCta)}
              enableToc
              inlineCta={inlineCta}
              locale={locale}
            />

            {post.tags.length > 0 ? (
              <div className="mx-auto mt-10 max-w-3xl border-t border-[var(--border)] pt-8 sm:mt-12">
                <div className="mb-3 flex flex-wrap items-center gap-2">
                  <Tag className="size-4 shrink-0 text-[var(--text-sm)]" aria-hidden />
                  <span className="text-sm font-medium text-[var(--text)]">{isTr ? "Etiketler" : "Tags"}</span>
                </div>
                <ul className="flex flex-wrap gap-2">
                  {post.tags.map((tag) => (
                    <li key={tag.slug}>
                      <Link
                        to={tagPath(locale, tag.slug)}
                        rel="tag"
                        className={cn(
                          "inline-flex items-center rounded-full border border-[var(--border)] bg-[var(--card)] px-3 py-1.5",
                          "text-xs font-medium text-[var(--text-md)] no-underline transition",
                          "hover:border-enabase-blue/30 hover:text-accent-link"
                        )}
                      >
                        {tag.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            <BlogRelatedPosts slug={post.slug} locale={locale} />

            <div className="blog-article-page__comments mx-auto mt-12 max-w-3xl sm:mt-14">
              <BlogComments blogId={post.id} comments={comments} locale={locale} />
            </div>

            <div className="mx-auto mt-10 max-w-3xl">
              <Link
                to={blogListPath(locale)}
                className="inline-flex items-center gap-2 text-sm font-semibold text-accent-link no-underline transition hover:opacity-80"
              >
                <ArrowLeft className="size-4" />
                {isTr ? "Tüm yazılara dön" : "Back to all posts"}
              </Link>
            </div>
          </article>
        ) : null}
      </Container>
    </div>
  );
}
